import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { PayspotDetailsCard, PayspotDetailsCardTitle } from '../PayspotDetailsCard';
import { getPayspotUnsuccessfulBillsBreakdown } from 'admin/actions/devices';
import CopyToClipboard from 'admin/components/atoms/CopyToClipboard';
import { formatDate, formatNum, formatUUID, toQueryParams } from 'admin/utils';
import Pager from '../../../../../shared/Pager';

const BillList = styled.div`
  height: 500px;
  overflow-y: auto;
`;

const Center = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PaginationWrapper = styled.div`
  margin: 16px 0;

  ul {
    margin: 0;
  }
`;

const BillItemContainer = styled.div`
  border-bottom: 1px solid #e5e5e5;
  padding: 12px 16px;

  &:hover {
    background-color: #f5f5f5;
  }
`;

const BillItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
  font-size: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const TitleText = styled.span`
  font-size: 14px;
  font-weight: 500;
`;

const AmountText = styled.span`
  font-size: 16px;
  color: #00a23f;
  margin-left: 2px;
  font-weight: 600;
`;

const NetworkStatusText = styled.span`
  font-size: 11px;

  .invalid {
    color: red;
  }

  .unknown {
    color: gray;
  }
`;

const TransactionLink = styled(Link)`
  color: #00a23f;

  &:hover {
    text-decoration: underline;
  }
`;

const UuidContainer = styled.div`
  min-height: 44px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  font-size: 11px;
  gap: 2px;
`;

const CopyIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 4px;

  svg {
    font-size: 11px !important;
    width: 11px !important;
    height: 11px !important;
  }
`;

type BillsBreakdownCardProps = {
  title: string;
  payspotId: string;
  from?: string | null;
  to?: string | null;
};

type BillItem = {
  breakdown_uuid: string;
  cashtray_uuid?: string | null;
  transaction_uuid?: string | null;
  currency: string;
  value: number;
  payspot_error_message?: string[] | null;
  payspot_network_status?: number | null;
  payspot_server_status?: number | null;
  created_at: string;
  updated_at: string;
};

type PaginationInfo = {
  current: number;
  has_next: boolean;
  has_prev: boolean;
  max_page: number;
  per_page: number;
};

type BillsData = {
  rows: BillItem[];
  count: number;
  pagination: PaginationInfo;
};

const emptyBillsData: BillsData = {
  rows: [],
  count: 0,
  pagination: {
    current: 1,
    has_next: false,
    has_prev: false,
    max_page: 1,
    per_page: 50,
  },
};

const BillsBreakdownCard = ({ title, payspotId, from, to }: BillsBreakdownCardProps) => {
  const [billsData, setBillsData] = useState<BillsData>(emptyBillsData);

  const NoData = <span style={{ color: 'gray', fontStyle: 'italic' }}>データなし</span>;

  const fetchBillsBreakdown = (id: string, pagination: { page?: number } = {}) => {
    const params: { [key: string]: string | number } = {
      page: 1,
      per_page: 50,
      ...pagination,
    };
    if (from) params.from = from;
    if (to) params.to = to;

    getPayspotUnsuccessfulBillsBreakdown(id, toQueryParams(params))
      .then((response: BillsData) => setBillsData(response))
      .catch((error) => {
        console.error('Failed to fetch bills breakdown:', error);
        setBillsData(emptyBillsData);
      });
  };

  useEffect(() => {
    fetchBillsBreakdown(payspotId);
  }, [payspotId, from, to]);

  const onChangePage = (page: number) => fetchBillsBreakdown(payspotId, { page });

  const getTransactionText = (
    cashtrayUuid?: string | null,
    transactionUuid?: string | null
  ): string => {
    if (cashtrayUuid) {
      return transactionUuid ? '取引成功' : '取引失敗';
    }
    return '不明';
  };

  const getPayspotErrorMessage = (errorMessages: string[]) => {
    return errorMessages.map((msg) => _parseErrorMessage(msg)).join('、 ');
  };

  const _parseErrorMessage = (errorMessage: string) => {
    const error = errorMessage.split('.')[1];
    switch (error) {
      case 'billval':
        return '紙幣受け入れ機エラー';
      case 'timeout':
        return 'タイムアウト';
      default:
        return '処理未了';
    }
  };

  const getNetworkStatusText = (status?: number | null) => {
    const statusText = {
      null: { label: '不明', className: 'unknown' },
      1: { label: '有効', className: 'valid' },
      0: { label: '無効', className: 'invalid' },
    };

    const { label, className } = statusText[status ?? 'null'];
    return (
      <>
        <span style={{ fontWeight: 'bold' }}>ネットワーク状態:</span>{' '}
        <span className={className}>{label}</span>
      </>
    );
  };
  const renderBillItems = () => {
    const { rows } = billsData;

    if (rows.length === 0) {
      return <Center>{NoData}</Center>;
    }

    return rows.map((item: BillItem) => {
      const {
        breakdown_uuid,
        cashtray_uuid,
        transaction_uuid,
        currency,
        value,
        payspot_error_message,
        payspot_network_status,
        created_at,
      } = item;

      return (
        <BillItemContainer key={breakdown_uuid}>
          <BillItemRow>
            <TitleText>{formatDate(created_at)}</TitleText>
            <TitleText>{getTransactionText(cashtray_uuid, transaction_uuid)}</TitleText>
          </BillItemRow>
          <BillItemRow>
            <div>
              <span style={{ color: 'grey' }}>{currency || 'JPY'}</span>
              <AmountText>{formatNum(value || 0)}</AmountText>
              <div style={{ gap: 8, display: 'flex', flexDirection: 'row' }}>
                <span style={{ fontSize: 11 }}>
                  <span style={{ fontWeight: 'bold' }}>エラー: </span>
                  {payspot_error_message ? getPayspotErrorMessage(payspot_error_message) : '不明'}
                </span>
                <NetworkStatusText>
                  {getNetworkStatusText(payspot_network_status)}
                </NetworkStatusText>
              </div>
            </div>
            <UuidContainer>
              {transaction_uuid && (
                <div>
                  <span style={{ fontWeight: 'bold' }}>取引ID: </span>
                  <TransactionLink to={`/transactions/${transaction_uuid}`}>
                    {formatUUID(transaction_uuid)}
                  </TransactionLink>
                </div>
              )}
              {cashtray_uuid && (
                <div>
                  <span style={{ fontWeight: 'bold' }}>金庫ID: </span>
                  <span>{formatUUID(cashtray_uuid)}</span>
                  <CopyIconWrapper>
                    <CopyToClipboard text={cashtray_uuid} tooltipId={cashtray_uuid} />
                  </CopyIconWrapper>
                </div>
              )}
            </UuidContainer>
          </BillItemRow>
        </BillItemContainer>
      );
    });
  };

  const { pagination } = billsData;

  return (
    <PayspotDetailsCard>
      <PayspotDetailsCardTitle>{title}</PayspotDetailsCardTitle>
      <BillList>{renderBillItems()}</BillList>
      <PaginationWrapper>
        <Pager pagination={pagination} onChangePage={onChangePage} size="sm" />
      </PaginationWrapper>
    </PayspotDetailsCard>
  );
};

export default BillsBreakdownCard;
